import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from "react";
import {
  ActionsSocketNames,
  ChatStatusFilter,
  IChat,
  IFulfilledChat,
  ISocketMessage,
  ISocketResponse,
  MessageActions,
  MessageOperation,
  MessageTypeNames,
  TMessage,
  TUserShort
} from "../../interfaces/interfacesChat";
import { ChatsAPI } from "../../API/chatsApi";
import { useContextHospi } from "../ContextHospi";
import { createFulfilledChats, getChatPartnerId } from "../../utils/chat";
import { UserAPI } from "../../API/userApi";
import { API } from "aws-amplify";

import {
  CHAT_INITIAL_STATE,
  ChatActions,
  chatReducer,
  IChatState,
  TChatAction
} from "./reducer";
import { Socket, SocketEvents } from "../../utils/Socket";
import { useSocket } from "../SocketProvider";
import { formatToServerDateTime } from "../../utils/time";
import { useQueryClient } from "@tanstack/react-query";
import {
  getMatchQueryKey,
  getStudentMatchesQueryKey
} from "../../API/queries/studentApiQueries";
import { MatchStatusEnum } from "../../utils/constants";
import { IMatch } from "../../interfaces/intarfaces";

type Reducer<S, A> = (prevState: S, action: A) => S;

interface IContextChat {
  state: IChatState;
  setActiveChatId: (id: string | null) => void;
  loadMoreMessages: () => void;
  readMessage: (message: {
    author_id: string;
    chat_id: string;
    created_at: string;
  }) => void;
  editMessage: (message: {
    author_id: string;
    chat_id: string;
    created_at: string;
    body: { text: string };
  }) => void;
  deleteMessage: (message: {
    author_id: string;
    chat_id: string;
    created_at: string;
    body: { text: string };
  }) => void;
  sendMessage: (message: {
    author_id: string;
    chat_id: string;
    body: { text: string };
  }) => void;
}

export type TChatReducer = Reducer<IChatState, TChatAction>;

const ContextChat = React.createContext<IContextChat>({
  state: CHAT_INITIAL_STATE,
  setActiveChatId: () => undefined,
  loadMoreMessages: () => undefined,
  readMessage: () => undefined,
  editMessage: () => undefined,
  deleteMessage: () => undefined,
  sendMessage: () => undefined
});

const reduxLogger = <S, A, R extends Reducer<S, A>>(reducer: R) => {
  return (state: S, action: A): S => {
    // console.log("reduxLogger:old state", action, state);
    const newState = reducer(state, action);
    // console.log("reduxLogger:new state", action, newState);
    return newState;
  };
};

export const useChat = () => {
  return useContext(ContextChat);
};

const useCacheHelpers = () => {
  const queryClient = useQueryClient();
  const { currentUserRole, currentUserId } = useContextHospi();

  const invalidateMatch = useCallback(
    (chat: IFulfilledChat | undefined) => {
      if (!chat) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: getMatchQueryKey({
          studentId: chat.student_id,
          hostId: chat.host_id,
          houseId: chat.house_id
        })
      });
      if (currentUserRole === "student") {
        queryClient.invalidateQueries({
          queryKey: getStudentMatchesQueryKey({ userId: currentUserId })
        });
      }
    },
    [queryClient, currentUserRole, currentUserId]
  );

  const changeMatchStatus = useCallback(
    (chat: IFulfilledChat | undefined, matchStatus: MatchStatusEnum) => {
      if (!chat) {
        return;
      }
      queryClient.setQueryData<IMatch>(
        getMatchQueryKey({
          studentId: chat.student_id,
          hostId: chat.host_id,
          houseId: chat.house_id
        }),
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              match_status: matchStatus
            };
          }
        }
      );
    },
    [queryClient, currentUserRole, currentUserId]
  );

  return useMemo(() => {
    return {
      invalidateMatch,
      changeMatchStatus
    };
  }, [invalidateMatch, changeMatchStatus]);
};

export const createReadMessageObject = (
  chatId: string,
  authorId: string,
  createdAt: string,
  propagate: boolean = false
) => {
  return {
    action: MessageActions.MESSAGE_READ,
    author_id: authorId,
    chat_id: chatId,
    created_at: createdAt,
    propagate,
    body: { text: "read" }
  };
};

const fulfillChats = async (
  chats: IChat[],
  userRole: string
): Promise<IFulfilledChat[] | null> => {
  const userIds = Array.from(
    new Set(chats.map((chat) => getChatPartnerId(chat, userRole)))
  );

  if (userIds.length === 0) {
    return null;
  }

  const {
    users_short_description: usersDescription
  }: {
    users_short_description: TUserShort[];
  } = await UserAPI.getShortDescription(userIds);

  return createFulfilledChats(chats, usersDescription, userRole);
};

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { currentUserId, currentUserRole } = useContextHospi();
  const { instance: socket } = useSocket();
  const cacheHelpers = useCacheHelpers();

  const [state, dispatch] = useReducer<TChatReducer, IChatState>(
    reduxLogger(chatReducer),
    CHAT_INITIAL_STATE,
    (value) => value
  );
  const stateRef = useRef(state);
  stateRef.current = state;

  const loadChats = useCallback(async () => {
    if (!currentUserId || !currentUserRole) return;

    dispatch({ type: ChatActions.SET_CHATS_IS_LOADING, payload: true });

    try {
      const allFulfilledChats: IFulfilledChat[] = [];
      let nextBatchToken = null;

      do {
        const searchResult: {
          chats: IChat[];
          next_batch_token: string | null;
        } = await ChatsAPI.getUserChats(
          currentUserId,
          ChatStatusFilter.ALL,
          100,
          nextBatchToken
        );

        const fulfilledChats = await fulfillChats(
          searchResult.chats,
          currentUserRole
        );

        if (fulfilledChats !== null) {
          allFulfilledChats.push(...fulfilledChats);
        }

        nextBatchToken = searchResult.next_batch_token;
      } while (nextBatchToken);

      dispatch({ type: ChatActions.SET_CHATS, payload: allFulfilledChats });
    } finally {
      dispatch({ type: ChatActions.SET_CHATS_IS_LOADING, payload: false });
    }
  }, [currentUserId, currentUserRole]);

  const loadChatCounters = useCallback(() => {
    ChatsAPI.getUserCounters().then((value) => {
      dispatch({
        type: ChatActions.SET_CHAT_COUNTERS,
        payload: value
      });
    });
  }, []);

  useEffect(() => {
    if (currentUserId && currentUserRole) {
      loadChats();
      loadChatCounters();
      dispatch({
        type: ChatActions.SET_USER_ROLE,
        payload: currentUserRole
      });
    } else {
      dispatch({ type: ChatActions.RESET, payload: null });
    }
  }, [currentUserId, currentUserRole]);

  useEffect(() => {
    if (state.activeChatId && currentUserId && socket) {
      dispatch({
        type: ChatActions.SET_ACTIVE_CHAT_MESSAGES_IS_LOADING,
        payload: true
      });
      dispatch({
        type: ChatActions.SET_CHAT_UNREAD_COUNT,
        payload: {
          chatId: state.activeChatId,
          count: 0
        }
      });
      const messagesPromise = ChatsAPI.getChatMessages(state.activeChatId);
      messagesPromise
        .then((result) => {
          dispatch({
            type: ChatActions.SET_ACTIVE_CHAT_MESSAGES,
            payload: result.messages
          });
          for (const message of result.messages) {
            if (message.author_id !== currentUserId) {
              // always send read event, even for read messages,
              // to read all previous messages which could be not read with past version of api
              socket.send(
                createReadMessageObject(
                  message.chat_id,
                  currentUserId,
                  message.created_at,
                  true
                )
              );
              break;
            }
          }
        })
        .finally(() => {
          dispatch({
            type: ChatActions.SET_ACTIVE_CHAT_MESSAGES_IS_LOADING,
            payload: false
          });
        });

      return () => {
        API.cancel(messagesPromise);
      };
    } else {
      dispatch({ type: ChatActions.SET_ACTIVE_CHAT_MESSAGES, payload: [] });
    }
  }, [state.activeChatId, currentUserId, socket]);

  const loadMoreMessages = useCallback(() => {
    if (state.isLoadingActiveChatMessages || state.isLoadingMoreChatMessages) {
      return;
    }

    const activeChatId = state.activeChatId;
    if (activeChatId && state.activeChatMessages.messages.length > 0) {
      const lastMessage = state.activeChatMessages.messages[0];
      const beforeDt = lastMessage ? lastMessage.created_at : null;
      if (!beforeDt) {
        return;
      }
      dispatch({
        type: ChatActions.SET_MORE_CHAT_MESSAGES_IS_LOADING,
        payload: true
      });
      ChatsAPI.getChatMessages(activeChatId, beforeDt)
        .then((result) => {
          dispatch({
            type: ChatActions.ADD_ACTIVE_CHAT_MESSAGES,
            payload: {
              messages: result.messages,
              chatId: activeChatId
            }
          });
        })
        .finally(() => {
          dispatch({
            type: ChatActions.SET_MORE_CHAT_MESSAGES_IS_LOADING,
            payload: false
          });
        });
    }
  }, [
    state.activeChatId,
    state.activeChatMessages,
    state.isLoadingActiveChatMessages,
    state.isLoadingMoreChatMessages
  ]);

  useEffect(() => {
    if (socket && currentUserId) {
      const messageHandler = (message: ISocketResponse) => {
        const state = stateRef.current;

        const messageChat = state.chats.find(
          (c) => c.chat_id === message?.chat_id
        );

        if (message.action === ActionsSocketNames.TEXT_MESSAGE_CREATED) {
          dispatch({
            type: ChatActions.PROCESS_NEW_MESSAGE,
            payload: {
              trace_id: message.trace_id,
              ...message.body
            }
          });
          if (
            message.body.chat_id === state.activeChatId &&
            message.body.author_id !== currentUserId
          ) {
            socket.send(
              createReadMessageObject(
                message.body.chat_id,
                currentUserId,
                message.body.created_at
              )
            );
          }
          if (message.body.msg_type === MessageTypeNames.REMOVE_TENANT) {
            cacheHelpers.invalidateMatch(messageChat);
          }
        } else if (message.action === ActionsSocketNames.TEXT_MESSAGE_READ) {
          dispatch({
            type: ChatActions.PROCESS_READ_MESSAGE,
            payload: message.body
          });
        } else if (message.action === ActionsSocketNames.TEXT_MESSAGE_UPDATED) {
          dispatch({
            type: ChatActions.UPDATE_MESSAGE,
            payload: message.body
          });
        } else if (message.action === ActionsSocketNames.TEXT_MESSAGE_DELETED) {
          dispatch({
            type: ChatActions.UPDATE_MESSAGE,
            payload: message.body
          });
        } else if (
          message.action === ActionsSocketNames.INVITE_MESSAGE_CREATED
        ) {
          dispatch({
            type: ChatActions.PROCESS_NEW_MESSAGE,
            payload: message.body
          });
          cacheHelpers.invalidateMatch(messageChat);
          if (message.body.chat_id === state.activeChatId) {
            socket.send(
              createReadMessageObject(
                message.body.chat_id,
                currentUserId,
                message.body.created_at
              )
            );
          }
        } else if (
          message.action === ActionsSocketNames.INVITE_MESSAGE_DELETED
        ) {
          dispatch({
            type: ChatActions.REMOVE_MESSAGE,
            payload: message.body
          });
          cacheHelpers.invalidateMatch(messageChat);
        } else if (message.action === ActionsSocketNames.INVITE_DATES_UPDATED) {
          dispatch({
            type: ChatActions.UPDATE_MESSAGE,
            payload: message.body
          });
          cacheHelpers.invalidateMatch(messageChat);
        } else if (message.action === ActionsSocketNames.RENT_DATES_UPDATED) {
          dispatch({
            type: ChatActions.UPDATE_MESSAGE,
            payload: message.body
          });
          cacheHelpers.invalidateMatch(messageChat);
        } else if (message.action === ActionsSocketNames.CHAT_CREATED) {
          // fetch chat and add to the list
          if (!state.chats.find((c) => c.chat_id === message.chat_id)) {
            ChatsAPI.getChat(message.chat_id).then((chat) => {
              fulfillChats([chat], state.userRole!).then((fulfilledChats) => {
                if (fulfilledChats?.[0]) {
                  dispatch({
                    type: ChatActions.ADD_CHAT,
                    payload: fulfilledChats?.[0]
                  });
                }
              });
            });
          }
        } else if (message.action === ActionsSocketNames.CHAT_DELETED) {
          dispatch({
            type: ChatActions.REMOVE_CHAT,
            payload: {
              chatId: message.chat_id
            }
          });
          cacheHelpers.invalidateMatch(messageChat);
        } else if (message.action === ActionsSocketNames.CHAT_REMOVED) {
          const updatingChat = state.chats.find(
            (c) => c.chat_id === message.chat_id
          );

          if (updatingChat) {
            const isHostAction =
              updatingChat.host_id === message.body.author_id;
            const isStudentAction =
              updatingChat.student_id === message.body.author_id;

            if (
              (state.userRole === "student" && isStudentAction) ||
              (state.userRole === "host" && isHostAction)
            ) {
              dispatch({
                type: ChatActions.REMOVE_CHAT,
                payload: {
                  chatId: message.chat_id
                }
              });
            }
          }
        } else if (message.action === ActionsSocketNames.CHAT_UNREMOVED) {
          const updatingChat = state.chats.find(
            (c) => c.chat_id === message.chat_id
          );
          if (updatingChat) {
            const isHostAction =
              updatingChat.host_id === message.body.author_id;
            const isStudentAction =
              updatingChat.student_id === message.body.author_id;

            if (
              (state.userRole === "student" && isStudentAction) ||
              (state.userRole === "host" && isHostAction)
            ) {
              dispatch({
                type: ChatActions.UNREMOVE_CHAT,
                payload: {
                  chatId: message.body.chat_id
                }
              });
            }
          }
        } else if (message.action === ActionsSocketNames.CHAT_ACCEPTED) {
          dispatch({
            type: ChatActions.ACCEPT_CHAT,
            payload: {
              chatId: message.chat_id
            }
          });
          cacheHelpers.changeMatchStatus(messageChat, MatchStatusEnum.CHATTED);
        }
      };

      socket.on(SocketEvents.MESSAGE, messageHandler);
      return () => {
        socket.off(SocketEvents.MESSAGE, messageHandler);
      };
    }
  }, [socket, currentUserId]);

  return (
    <ContextChat.Provider
      value={{
        state,
        setActiveChatId: useCallback((chatId: string | null) => {
          dispatch({
            type: ChatActions.SET_ACTIVE_CHAT_ID,
            payload: chatId
          });
        }, []),
        loadMoreMessages,
        readMessage: useCallback(
          (message) => {
            if (!socket) {
              return;
            }
            socket.send(
              createReadMessageObject(
                message.chat_id,
                message.author_id,
                message.created_at
              )
            );
            socket.send({
              action: MessageActions.MESSAGE_READ,
              ...message
            });
          },
          [socket]
        ),
        editMessage: useCallback(
          (message) => {
            if (!socket) {
              return;
            }
            dispatch({
              type: ChatActions.CHANGE_MESSAGE_OPERATION,
              payload: {
                created_at: message.created_at,
                chat_id: message.chat_id,
                operation: MessageOperation.UPDATING
              }
            });
            socket.send({
              action: MessageActions.MESSAGE_UPDATE,
              ...message
            });
          },
          [socket]
        ),
        deleteMessage: useCallback(
          (message) => {
            if (!socket) {
              return;
            }
            dispatch({
              type: ChatActions.CHANGE_MESSAGE_OPERATION,
              payload: {
                created_at: message.created_at,
                chat_id: message.chat_id,
                operation: MessageOperation.DELETING
              }
            });
            socket.send({
              action: MessageActions.MESSAGE_DELETE,
              ...message
            });
          },
          [socket]
        ),
        sendMessage: useCallback(
          (message) => {
            if (!socket) {
              return;
            }
            const traceId = Socket.createTraceId();
            socket.send({
              trace_id: traceId,
              action: MessageActions.MESSAGE_NEW,
              is_read: true,
              ...message
            } as ISocketMessage);

            const createdAt = formatToServerDateTime(new Date());

            dispatch({
              type: ChatActions.ADD_TEMP_MESSAGE,
              payload: {
                ...message,
                is_read: true,
                trace_id: traceId,
                operation: MessageOperation.CREATING,
                created_at: createdAt,
                updated_at: createdAt
              } as TMessage
            });
          },
          [socket]
        )
      }}
    >
      {children}
    </ContextChat.Provider>
  );
};
